import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { YardBlockDto } from '@yard/app/api'
import { FormProps } from '@yard/app/models/form-props.model'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import {
  HousekeepingPlanFillDirection,
  HousekeepingPlanSequencingStrategy,
  ICreateHousekeepingPlanFormData,
} from '../../../models/yard-setup.model'
import { CreateHousekeepingPlanFormStore } from '../../../stores/create-housekeeping-plan.form-store'

interface Props extends FormProps {
  formStore: CreateHousekeepingPlanFormStore
  blocks: YardBlockDto[]
  hasDeficit: boolean
  onSubmit: (data: ICreateHousekeepingPlanFormData) => void
}

// Fill direction is hidden until the BE branches on RowFillDirection. The
// enum carries BayFirst + RowFirst but no code anywhere consumes the value
// today, so the dropdown is a false affordance — the dispatched WI order is
// the same regardless of what the user picks. The form store still defaults
// the field to BayFirst so the BE's required RowFillDirection keeps flowing
// through. Flip to `true` and `git grep RowFillDirection` to find the
// behavioral consumer once it lands.
const SHOW_FILL_DIRECTION = false

export const CreateHousekeepingPlanForm = observer(
  ({ id, formStore, blocks, hasDeficit, onSubmit }: Props) => {
    const { t } = useTranslate()
    const theme = useMinimalsTheme()

    // Reset dispatchWhatFits when the deficit clears so the toggle's stored
    // state can never drift from what the user sees. With the toggle hidden
    // in the no-deficit case, persisting a stale `true` would mean the user
    // thinks the form is in default state while the (invisible) value still
    // says "truncate" — the BE ignores it when there's no deficit, but the
    // displayed-vs-actual mismatch would confuse anyone reading the form.
    useEffect(() => {
      if (!hasDeficit && formStore.dispatchWhatFits.data) {
        formStore.dispatchWhatFits.setValue(false)
      }
    }, [hasDeficit, formStore])

    const handleSubmit = () => {
      if (formStore.validate()) {
        onSubmit(formStore.data)
      }
    }

    return (
      <Grid
        id={id}
        component='form'
        autoComplete='off'
        noValidate
        container
        onSubmit={event => {
          event.preventDefault()
          handleSubmit()
        }}
        spacing={theme.customSpacing.m}
      >
        <Grid size={12}>
          <TextField
            {...formStore.name.props}
            fullWidth
            label={t('planName', 'Plan name (optional)')}
            name='name'
          />
        </Grid>

        <Grid size={12}>
          <FormControl fullWidth required error={!!formStore.blockId.error}>
            <InputLabel>{t('block', 'Block')}</InputLabel>
            <Select {...formStore.blockId.props} label={t('block', 'Block')} name='blockId'>
              {blocks.map(block => (
                <MenuItem key={`block_${block.id}`} value={block.id}>
                  {block.name}
                </MenuItem>
              ))}
            </Select>
            {formStore.blockId.error && (
              <Typography variant='caption' color='error'>
                {t(formStore.blockId.error)}
              </Typography>
            )}
          </FormControl>
        </Grid>

        {/*
        Sequencing strategy is hidden until a second strategy exists. Today
        the enum carries only OperatorConvenience and the BE has no
        branching on the value, so the dropdown is a false affordance.
        The form store still defaults the field to OperatorConvenience so
        the BE's required SequencingStrategy keeps flowing through.
        UN-HIDE when HousekeepingPlanSequencingStrategy gains a second member.
      */}
        {Object.keys(HousekeepingPlanSequencingStrategy).length > 1 && (
          <Grid size={12}>
            <FormControl fullWidth required error={!!formStore.sequencingStrategy.error}>
              <InputLabel>{t('sequencingStrategy', 'Sequencing strategy')}</InputLabel>
              <Select
                {...formStore.sequencingStrategy.props}
                label={t('sequencingStrategy', 'Sequencing strategy')}
                name='sequencingStrategy'
              >
                <MenuItem value={HousekeepingPlanSequencingStrategy.OperatorConvenience}>
                  {t('operatorConvenience', 'Operator convenience')}
                </MenuItem>
              </Select>
              {formStore.sequencingStrategy.error && (
                <Typography variant='caption' color='error'>
                  {t(formStore.sequencingStrategy.error)}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}

        {SHOW_FILL_DIRECTION && (
          <Grid size={12}>
            <FormControl fullWidth required error={!!formStore.fillDirection.error}>
              <InputLabel>{t('fillDirection', 'Fill direction')}</InputLabel>
              <Select
                {...formStore.fillDirection.props}
                label={t('fillDirection', 'Fill direction')}
                name='fillDirection'
              >
                <MenuItem value={HousekeepingPlanFillDirection.BayFirst}>
                  {t('bayFirst', 'Bay first')}
                </MenuItem>
                <MenuItem value={HousekeepingPlanFillDirection.RowFirst}>
                  {t('rowFirst', 'Row first')}
                </MenuItem>
              </Select>
              {formStore.fillDirection.error && (
                <Typography variant='caption' color='error'>
                  {t(formStore.fillDirection.error)}
                </Typography>
              )}
            </FormControl>
          </Grid>
        )}

        {/* Always reserve the toggle row so the dialog doesn't grow/shrink
            when deficit flips. The control itself is hidden in the no-deficit
            case to keep the affordance off until it's actionable. */}
        <Grid size={12} sx={{ minHeight: 42 }}>
          {hasDeficit && (
            <FormControlLabel
              control={
                <Switch
                  checked={formStore.dispatchWhatFits.data}
                  onChange={event => formStore.dispatchWhatFits.setValue(event.target.checked)}
                  name='dispatchWhatFits'
                />
              }
              label={t(
                'dispatchWhatFits',
                'Dispatch what fits (truncate to capacity instead of erroring)',
              )}
            />
          )}
        </Grid>
      </Grid>
    )
  },
)
